import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useGetMyChildrenQuery, useGetMyPoliciesQuery, useListMyMessagesQuery, useListMyObservationsQuery } from '../store/api/localApi';
import { MessageDeliveryStatus, MessageType, ParentSafePolicy, StaffPolicyRow } from '../types';

export interface ChildAttention { forms: number; registration: number; policies: number; observations: number; messages: number; total: number }

type ChildLike = { id: string; status: 'PENDING_START' | 'ACTIVE' | 'LEFT'; badges: { pendingForms: number; registrationPending: boolean } };
type ObservationLike = { studentId: string; status: string };
type MessageLike = { studentId?: string; type: MessageType; status: string };

/**
 * Things waiting per child — the same rules as buildNeedsYouRows: a LEFT child
 * is read-only (zero), and registration pending replaces the forms count
 * because the registration invitation is already inside pendingForms.
 * Announcements belong to the family, so they count for no child.
 */
export function computeChildAttention(children: ChildLike[], policies: Array<ParentSafePolicy | StaffPolicyRow>, observations: ObservationLike[], messages: MessageLike[]): Record<string, ChildAttention> {
  const out: Record<string, ChildAttention> = {};
  for (const c of children) {
    if (c.status === 'LEFT') { out[c.id] = { forms: 0, registration: 0, policies: 0, observations: 0, messages: 0, total: 0 }; continue; }
    const a: ChildAttention = {
      forms: c.badges.registrationPending ? 0 : c.badges.pendingForms,
      registration: c.badges.registrationPending ? 1 : 0,
      policies: policies.filter((p) => 'outstandingForChildren' in p && p.outstandingForChildren.some((o) => o.childId === c.id)).length,
      observations: observations.filter((o) => o.studentId === c.id && o.status === MessageDeliveryStatus.UNREAD).length,
      messages: messages.filter((m) => m.studentId === c.id && m.type === MessageType.PARENT_DM && m.status === MessageDeliveryStatus.UNREAD).length,
      total: 0,
    };
    a.total = a.forms + a.registration + a.policies + a.observations + a.messages;
    out[c.id] = a;
  }
  return out;
}

export function useChildAttention() {
  const { isParent } = useAuth();
  const opts = { skip: !isParent, pollingInterval: 300_000, skipPollingIfUnfocused: true, refetchOnFocus: true };
  const children = useGetMyChildrenQuery(undefined, { skip: !isParent });
  const policies = useGetMyPoliciesQuery(undefined, opts);
  const observations = useListMyObservationsQuery({ limit: 50 }, opts);
  const messages = useListMyMessagesQuery({ limit: 50 }, opts);
  const byChild = useMemo(
    () => computeChildAttention(children.data?.children ?? [], policies.data?.policies ?? [], observations.data?.deliveries ?? [], messages.data?.deliveries ?? []),
    [children.data, policies.data, observations.data, messages.data]
  );
  const totalExcluding = (childId?: string) =>
    Object.entries(byChild).reduce((n, [id, a]) => (id === childId ? n : n + a.total), 0);
  return { byChild, isLoading: children.isLoading || policies.isLoading || observations.isLoading || messages.isLoading, totalExcluding };
}
